import { useState } from 'react';
import { HelpCircle, ChevronDown, Zap } from 'lucide-react';

const faqs = [
  {
    category: 'The AI Assistant',
    color: 'green',
    items: [
      {
        q: 'How accurate is ElectrAI?',
        a: "ElectrAI is trained on millions of data points including datasheets, technical manuals, open-source hardware documentation, and maker community logs. It's incredibly accurate for component identifying, explaining circuit behavior, and debugging common firmware issues. However, always exercise caution and use a multimeter to verify before applying power to a circuit."
      },
      {
        q: "What's the difference between Free, Repair Pass, and Pro?",
        a: "Guests receive 5 free diagnostic sessions to test the platform. The 7-Day Repair Pass ($4.99) gives you 7 days of unlimited diagnostics and schematics for quick bench projects without a recurring commitment. ElectrAI Pro ($9.99/mo) unlocks unlimited diagnostics, secure project saving in your Vault, component lookups, and master-tier PCB/firmware code generation."
      },
      {
        q: 'How do I use ElectrAI for component ID?',
        a: "You can describe the markings on a chip, or better yet, if you're on a mobile device, take a high-quality photo of the board. ElectrAI can often identify ICs, transistor packages, and SMD components from their visible codes and traces."
      }
    ]
  },
  {
    category: 'Component & Schematic Search',
    color: 'cyan',
    items: [
      {
        q: 'Is the component database updated?',
        a: "Yes, we index new parts daily. We aggregate data from major distributors, manufacturer datasheets, and open libraries to give you the most comprehensive lookup possible."
      },
      {
        q: 'Can I find pinouts here?',
        a: "Absolutely. Our Schematics section is designed specifically for finding pinout diagrams, typical application circuits, and datasheet references. Just search for the part number or name."
      }
    ]
  },
  {
    category: 'Projects & Learning',
    color: 'purple',
    items: [
      {
        q: 'Can I submit my own DIY project?',
        a: "We are currently working on a community submission portal. For now, we manually curate projects from top-tier maker sources to ensure high-quality, safe, and working guides."
      },
      {
        q: 'Are the learning paths for beginners?',
        a: "Yes! Our 'Learn' section starts from the absolute basics like Ohm's Law and builds up to advanced microcontroller programming and PCB design. It's built for everyone from total novices to experienced engineers looking for a quick reference."
      }
    ]
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggle = (idx: string) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ padding: '2.5rem 0 2.5rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{
              width: '52px', height: '52px',
              background: 'rgba(0,255,136,0.1)',
              border: '1px solid rgba(0,255,136,0.3)',
              borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HelpCircle size={24} color="var(--color-primary)" />
            </div>
          </div>
          <h1 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Help & <span style={{ color: 'var(--color-primary)' }}>FAQs</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto', fontSize: '0.9rem' }}>
            Find answers to common questions about ElectrAI, component searching, and our electronics maker platform.
          </p>
        </div>

        {/* FAQ Sections */}
        {faqs.map((section, sIdx) => {
          const colors: Record<string, string> = {
            green: 'var(--color-primary)',
            cyan: 'var(--color-secondary)',
            purple: 'var(--color-purple)'
          };
          const color = colors[section.color];

          return (
            <div key={section.category} style={{ marginBottom: '2.5rem' }}>
              <h2 style={{ fontSize: '0.9rem', color: color, fontWeight: 800, letterSpacing: '0.1em', marginBottom: '1rem', paddingLeft: '0.5rem' }}>
                {section.category.toUpperCase()}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {section.items.map((item, iIdx) => {
                  const id = `${sIdx}-${iIdx}`;
                  const isOpen = openIndex === id;

                  return (
                    <div key={id} style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'border-color 0.2s ease',
                    }}
                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = `${color}33`}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
                    >
                      <button
                        onClick={() => toggle(id)}
                        style={{
                          width: '100%', padding: '1.25rem 1.5rem',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                          color: 'var(--text-primary)',
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{item.q}</span>
                        <ChevronDown size={18} style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                          transition: 'transform 0.3s ease',
                          color: isOpen ? color : 'var(--text-muted)'
                        }} />
                      </button>
                      
                      {isOpen && (
                        <div style={{
                          padding: '0 1.5rem 1.25rem',
                          color: 'var(--text-secondary)',
                          fontSize: '0.92rem',
                          lineHeight: 1.6,
                        }}>
                          <div style={{ height: '1px', background: 'var(--border-color)', marginBottom: '1rem' }} />
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Didn't find answer? */}
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          padding: '2rem',
          background: 'rgba(0,207,255,0.04)',
          border: '1px solid rgba(0,207,255,0.2)',
          borderRadius: '16px'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Still have questions?</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            The best way to learn how DIYElectronicMech works is to try it. Ask ElectrAI anything.
          </p>
          <button
            onClick={() => window.location.href = '/diagnose'}
            style={{
              padding: '0.625rem 1.5rem', background: 'var(--color-secondary)', color: '#050912',
              borderRadius: '8px', border: 'none', fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(0,207,255,0.4)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = 'none'}
          >
            Launch Diagnostics <Zap size={14} />
          </button>
        </div>

      </div>
    </div>
  );
}
